import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ChevronDown, Fence as FenceIcon } from 'lucide-react';
import { toast } from 'sonner';
import { calculatePricing } from '../services/pricingService';

const FenceCalculatorPanel = forwardRef(({
  isOpen, onToggle, measurementTotals, manualOverride, setManualOverride,
  onServiceTotalChange, initialInputs, onInputsChange,
}, ref) => {
  const [fenceData, setFenceData] = useState(
    initialInputs && Object.keys(initialInputs).length
      ? { linearFeet: '', height: '4', ...initialInputs }
      : { linearFeet: '', height: '4' }
  );

  const onInputsChangeRef = useRef(onInputsChange);
  onInputsChangeRef.current = onInputsChange;
  useEffect(() => { onInputsChangeRef.current?.('fence', fenceData); }, [fenceData]);

  // Auto-fill from measurement if panel is open and user hasn't manually edited
  useEffect(() => {
    if (!isOpen) return;
    const feet = measurementTotals?.lineFeet || 0;
    // Only auto-fill if: feet > 0 AND user hasn't manually overridden
    if (feet > 0 && !manualOverride.fenceFt) {
      // Round to whole number for clean UX display
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional sync from external measurement source
      setFenceData((prev) => ({ ...prev, linearFeet: Math.round(feet) }));
    }
  }, [isOpen, measurementTotals?.lineFeet, manualOverride.fenceFt]);

  const handleCalculate = async () => {
    const lf = Number(fenceData.linearFeet);
    if (!Number.isFinite(lf) || lf <= 0) {
      toast.error('Please enter fence linear feet.');
      return;
    }

    const resp = await calculatePricing('fence', {
      data: {
        linearFeet: lf,
        height: fenceData.height,
      },
      discount: 0,
    });

    if (!resp.success || !resp.quote) {
      toast.error(resp.message || 'Service pricing not implemented yet, but UI remains active.');
      onServiceTotalChange('fence', { subtotal: 0, tax: 0, grandTotal: 0 });
      return;
    }

    const quote = resp.quote;
    const nowIso = new Date().toISOString();

    const result = {
      serviceTotal: quote.serviceTotal || quote.subtotal || 0,
      adjustedFeet: quote.adjustedFeet || lf,
      discount: quote.discount || 0,
      tax: quote.tax || 0,
      subtotal: quote.subtotal || quote.serviceTotal || 0,
      grandTotal: quote.grandTotal || 0,
    };

    onServiceTotalChange('fence', {
      input: {
        linearFeet: lf,
        height: fenceData.height,
      },
      result,
      measurement: {
        polygons: null,
        linearFeet: lf,
        sqFt: null,
        computedAt: nowIso,
      },
    });
  };

  useImperativeHandle(ref, () => ({
    recalc: handleCalculate,
    getCurrentInputs: () => fenceData,
  }), [fenceData]);

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-secondary/10 rounded-lg">
              <FenceIcon className="h-5 w-5 text-secondary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-secondary">🚧 Fence Cleaning</h3>
              <p className="text-sm text-muted-foreground">Configure fence specifications</p>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-colors ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="p-6 space-y-4 bg-muted/30">
            {/* Linear Feet */}
            <div className="space-y-2">
              <Label htmlFor="fence-length">Linear Feet</Label>
              <Input
                id="fence-length"
                type="number"
                placeholder="Enter linear feet"
                className="font-mono"
                value={fenceData.linearFeet}
                onChange={(e) => {
                  setManualOverride(prev => ({ ...prev, fenceFt: true }));
                  setFenceData(prev => ({ ...prev, linearFeet: e.target.value }));
                }}
              />
            </div>

            {/* Height */}
            <div className="space-y-2">
              <Label htmlFor="fence-height">Average Height (ft)</Label>
              <Select
                value={fenceData.height}
                onValueChange={(value) => setFenceData(prev => ({ ...prev, height: value }))}
              >
                <SelectTrigger id="fence-height">
                  <SelectValue placeholder="Select height" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 ft</SelectItem>
                  <SelectItem value="6">6 ft</SelectItem>
                  <SelectItem value="8">8 ft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Discount removed at service level; handled in Quote Summary */}

            {/* Fence Type */}
            <div className="space-y-2">
              <Label htmlFor="fence-type">Fence Type</Label>
              <Select>
                <SelectTrigger id="fence-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wood">Wood</SelectItem>
                  <SelectItem value="vinyl">Vinyl</SelectItem>
                  <SelectItem value="chainlink">Chain Link</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Calculate Button */}
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              size="lg"
              onClick={handleCalculate}
            >
              Calculate Fence Cost
            </Button>
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

export default FenceCalculatorPanel;
